import type { CountryResponse } from './countries.contract';
import type { Country, LocalisedCountry } from './country.model';

const ALPHA_2 = /^[A-Z]{2}$/;

/** Keeps the rows that carry a usable alpha-2 code, codes upper-cased. */
export function toCountries(response: readonly CountryResponse[] | null | undefined): Country[] {
  const countries: Country[] = [];

  for (const row of response ?? []) {
    const alpha2 = (row.alpha2 ?? '').trim().toUpperCase();

    if (!ALPHA_2.test(alpha2)) {
      continue;
    }

    countries.push({
      alpha2,
      alpha3: (row.alpha3 ?? '').trim().toUpperCase(),
      name: (row.name ?? '').trim() || alpha2,
    });
  }

  return countries;
}

/**
 * Names each country in `language` (a backend language code: `en`, `no`,
 * `de`) through `Intl.DisplayNames`, falling back to the English name where
 * the browser has none, and sorts by the name shown.
 */
export function localiseCountries(
  countries: readonly Country[],
  language: string,
): LocalisedCountry[] {
  const names = regionNames(language);
  const collator = new Intl.Collator(language, { sensitivity: 'base' });

  return countries
    .map((country) => ({ ...country, label: displayName(names, country) }))
    .sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * Whether `query` finds `country`: a case- and accent-insensitive substring
 * of the shown or English name, or exactly its alpha-2 or alpha-3 code.
 */
export function matchesCountry(country: LocalisedCountry, query: string): boolean {
  const needle = fold(query);

  if (needle.length === 0) {
    return true;
  }

  return (
    fold(country.label).includes(needle) ||
    fold(country.name).includes(needle) ||
    country.alpha2.toLowerCase() === needle ||
    country.alpha3.toLowerCase() === needle
  );
}

/** A stored country in any case ("no", " NO") as the alpha-2 code a list holds. */
export function normaliseCountryCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function regionNames(language: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([language, 'en'], { type: 'region', fallback: 'none' });
  } catch {
    return null;
  }
}

function displayName(names: Intl.DisplayNames | null, country: Country): string {
  try {
    return names?.of(country.alpha2) || country.name;
  } catch {
    return country.name;
  }
}

/** Letters that do not decompose into a base letter and a diacritic. */
const LETTERS: Readonly<Record<string, string>> = {
  ø: 'o',
  æ: 'ae',
  œ: 'oe',
  ß: 'ss',
  ł: 'l',
  đ: 'd',
};

/**
 * Lower-case without diacritics, so "osterreich" finds "Österreich" and
 * "ost" finds "Østerrike".
 */
function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[øæœßłđ]/g, (letter) => LETTERS[letter] ?? letter);
}
